import { trpc } from "@/lib/trpc";
import { useGameCreator } from "@/contexts/GameCreatorContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  Rocket, CheckCircle2, XCircle, AlertTriangle, ExternalLink,
  Loader2, DollarSign, Users, Clock, Trophy, Share2, Copy, Check,
  ArrowRight, PartyPopper, Wallet,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link, useLocation } from "wouter";

interface LaunchGameModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const BASE_CREATION_FEE_CENTS = 2000;

export default function LaunchGameModal({ open, onOpenChange }: LaunchGameModalProps) {
  const { gameConfig } = useGameCreator();
  const entryFee = gameConfig.entryFee ?? 0;
  const [copied, setCopied] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [launchResult, setLaunchResult] = useState<any>(null);
  const [, navigate] = useLocation();

  const connectionQuery = trpc.gamepointApi.checkConnection.useQuery(undefined, {
    enabled: open,
  });

  // Creation fee economics — mirrors server/routers/gamepointApi.ts
  const subscriptionQuery = trpc.subscription.getMySubscription.useQuery(undefined, {
    enabled: open,
  });
  const walletQuery = trpc.wallet.getBalance.useQuery(undefined, { enabled: open });

  const credits = subscriptionQuery.data?.gameCreationCredits ?? 0;
  const tierDiscountPct = subscriptionQuery.data?.tierInfo?.gameFeePercent ?? 0;
  const baseFeeCents = Math.round(BASE_CREATION_FEE_CENTS * (1 - tierDiscountPct / 100));

  // Beta: while the BETA coupon is active it is pre-filled, so the event fee is waived on every event.
  const betaQuery = trpc.coupon.betaStatus.useQuery(undefined, { enabled: open, staleTime: 60_000 });
  const [couponTouched, setCouponTouched] = useState(false);
  useEffect(() => {
    if (open && !couponTouched && betaQuery.data?.active && !couponCode) {
      setCouponCode(betaQuery.data.code);
    }
  }, [open, couponTouched, betaQuery.data, couponCode]);

  const normalizedCoupon = couponCode.trim().toUpperCase();
  const couponPreview = trpc.coupon.previewCreateGame.useQuery(
    { code: normalizedCoupon },
    { enabled: open && normalizedCoupon.length > 0 && baseFeeCents > 0 }
  );
  const couponValid =
    normalizedCoupon.length > 0 &&
    couponPreview.data?.valid === true &&
    couponPreview.data.code === normalizedCoupon;
  const couponError =
    normalizedCoupon.length > 0 && couponPreview.data && !couponPreview.data.valid
      ? couponPreview.data.message
      : "";

  // Mirrors server/creationFee.ts: plan → valid coupon → free credit → wallet.
  const creationFeeCents =
    baseFeeCents <= 0
      ? 0
      : couponValid
        ? couponPreview.data?.finalAmount ?? baseFeeCents
        : credits > 0
          ? 0
          : baseFeeCents;
  const walletBalanceCents = walletQuery.data?.availableBalance ?? null;
  const insufficientForLaunch =
    creationFeeCents > 0 &&
    walletBalanceCents != null &&
    walletBalanceCents < creationFeeCents;
  // Don't launch while the beta code or a typed code is still being checked: the price shown could still change.
  const feeCheckPending =
    betaQuery.isLoading || (normalizedCoupon.length > 0 && baseFeeCents > 0 && couponPreview.isFetching);

  const validateMutation = trpc.gamepointApi.validateConfig.useMutation();
  const launchMutation = trpc.gamepointApi.launchGame.useMutation();

  // Clamp cuts to platform rules so AI-provided values can never produce an
  // invalid payload (winner split must stay >= 60%).
  const hostCut = Math.min(gameConfig.hostCut || 10, 15);
  const promoterCutRaw = Math.min(gameConfig.promoterCut || 10, 15);
  const promoterCut = Math.max(Math.min(promoterCutRaw, 20 - hostCut), 0);
  const winnerSplit = Math.max(100 - 15 - hostCut - promoterCut, 60);
  const buildPayload = () => ({
    gameName: gameConfig.title || "Untitled Event",
    gameType: gameConfig.gameType || "Creative Challenge",
    description: gameConfig.description || gameConfig.rules?.join(". ") || "",
    entryFee: Math.max(Math.round(entryFee * 100), 0), // convert dollars to cents; zero is a valid free game
    maxPlayers: gameConfig.maxPlayers || 100,
    hostSplit: hostCut,
    promoterSplit: promoterCut,
    winnerSplit: winnerSplit,
    platformSplit: 15,
    duration: gameConfig.duration || "1 hour",
    winCondition: gameConfig.winCondition || "",
    rules: gameConfig.rules || [],
    submissionType: gameConfig.submissionType || "text",
    votingMethod: gameConfig.votingMethod || "auto_score",
    socialDistributionMode: gameConfig.socialDistributionMode || "direct",
    socialHashtag: gameConfig.socialHashtag || "",
    socialEngagementWeight: gameConfig.socialEngagementWeight || 30,
    influencerChannelUrl: gameConfig.influencerChannelUrl || "",
    // Only a coupon the server has confirmed is sent; otherwise the credit or wallet pays.
    couponCode: couponValid ? normalizedCoupon : undefined,
  });

  const handleValidate = async () => {
    try {
      const result = await validateMutation.mutateAsync(buildPayload());
      if (result.valid) {
        toast.success("Event setup is valid!");
      } else {
        toast.error(`Validation issues: ${result.issues.join(", ")}`);
      }
    } catch (err: any) {
      toast.error(`Validation failed: ${err.message}`);
    }
  };

  const handleLaunch = async () => {
    try {
      const result = await launchMutation.mutateAsync(buildPayload());
      setLaunchResult(result);
      if (result.launched) {
        toast.success("Event launched!");
      } else {
        toast.error(result.message);
      }
    } catch (err: any) {
      toast.error(`Launch failed: ${err.message}`);
    }
  };

  const handleViewGame = () => {
    if (launchResult?.gameUrl) {
      onOpenChange(false);
      navigate(launchResult.gameUrl);
    }
  };

  const handleCopyPayload = () => {
    const payload = launchResult?.payload || validateMutation.data?.config;
    if (payload) {
      navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Event setup copied to clipboard");
    }
  };

  const handleCopyGameLink = () => {
    if (launchResult?.gameUrl) {
      const fullUrl = `${window.location.origin}${launchResult.gameUrl}`;
      navigator.clipboard.writeText(fullUrl);
      toast.success("Event link copied!");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg glass-panel border-border/50">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {launchResult?.launched ? (
              <>
                <PartyPopper className="w-5 h-5 text-gold" />
                Event launched!
              </>
            ) : (
              <>
                <Rocket className="w-5 h-5 text-neon" />
                Launch on GamePoint
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {launchResult?.launched
              ? entryFee > 0
                ? "Saved — an administrator will review your paid event shortly"
                : "Your event is live and ready for players"
              : "Create this event on GamePoint"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Success State — show after launch */}
          {launchResult?.launched ? (
            <>
              <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20 space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                  <p className="text-sm font-semibold text-green-300">{launchResult.message}</p>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="p-2 rounded bg-green-500/10">
                    <p className="text-[10px] text-green-400/70 uppercase">Title</p>
                    <p className="text-xs font-medium text-green-200 truncate">{gameConfig.title}</p>
                  </div>
                  <div className="p-2 rounded bg-green-500/10">
                    <p className="text-[10px] text-green-400/70 uppercase">Ticket price</p>
                    <p className="text-xs font-mono font-medium text-green-200">
                      {entryFee > 0 ? `$${entryFee.toFixed(2)}` : "Free"}
                    </p>
                  </div>
                  <div className="p-2 rounded bg-green-500/10">
                    <p className="text-[10px] text-green-400/70 uppercase">Max Players</p>
                    <p className="text-xs font-mono font-medium text-green-200">{gameConfig.maxPlayers}</p>
                  </div>
                  <div className="p-2 rounded bg-green-500/10">
                    <p className="text-[10px] text-green-400/70 uppercase">Status</p>
                    {entryFee > 0 ? (
                      <Badge variant="outline" className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-[10px]">
                        Pending approval
                      </Badge>
                    ) : (
                      <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[10px]">Active</Badge>
                    )}
                  </div>
                </div>


                {launchResult.externalUrl && (
                  <a
                    href={launchResult.externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-neon hover:underline flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Also live on game-point.co
                  </a>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs gap-1.5"
                  onClick={handleCopyGameLink}
                >
                  <Copy className="w-3.5 h-3.5" />
                  Copy Link
                </Button>
                <Button
                  size="sm"
                  className="flex-1 bg-neon hover:bg-neon/90 text-background text-xs gap-1.5"
                  onClick={handleViewGame}
                >
                  <ArrowRight className="w-3.5 h-3.5" />
                  View your event
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Connection status for the optional external API — only shown when one is configured */}
              {connectionQuery.data?.configured && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-secondary/30">
                {connectionQuery.isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                ) : connectionQuery.data?.connected ? (
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                ) : connectionQuery.data?.configured ? (
                  <XCircle className="w-4 h-4 text-red-400" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-gold" />
                )}
                <span className="text-sm">
                  {connectionQuery.isLoading
                    ? "Checking connection..."
                    : connectionQuery.data?.message}
                </span>
              </div>
              )}
              {connectionQuery.data?.configured && <Separator className="bg-border/30" />}

              {/* Game Summary */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold">Event summary</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-2.5 rounded-lg bg-secondary/20">
                    <p className="text-[10px] text-muted-foreground uppercase">Title</p>
                    <p className="text-sm font-medium truncate">{gameConfig.title || "Untitled"}</p>
                  </div>
                  <div className="p-2.5 rounded-lg bg-secondary/20">
                    <p className="text-[10px] text-muted-foreground uppercase">Type</p>
                    <p className="text-sm font-medium truncate">{gameConfig.gameType || "Not set"}</p>
                  </div>
                  <div className="p-2.5 rounded-lg bg-secondary/20 flex items-center gap-1.5">
                    <DollarSign className="w-3.5 h-3.5 text-gold" />
                    <div>
                      <p className="text-[10px] text-muted-foreground">Ticket price</p>
                      <p className="text-sm font-mono font-medium">
                        {entryFee > 0 ? `$${entryFee.toFixed(2)}` : "Free"}
                      </p>
                    </div>
                  </div>
                  <div className="p-2.5 rounded-lg bg-secondary/20 flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5 text-neon" />
                    <div>
                      <p className="text-[10px] text-muted-foreground">Max Players</p>
                      <p className="text-sm font-mono font-medium">{gameConfig.maxPlayers || 0}</p>
                    </div>
                  </div>
                  <div className="p-2.5 rounded-lg bg-secondary/20 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                    <div>
                      <p className="text-[10px] text-muted-foreground">Duration</p>
                      <p className="text-sm font-medium">{gameConfig.duration || "Not set"}</p>
                    </div>
                  </div>
                  <div className="p-2.5 rounded-lg bg-secondary/20 flex items-center gap-1.5">
                    <Trophy className="w-3.5 h-3.5 text-gold" />
                    <div>
                      <p className="text-[10px] text-muted-foreground">Winner Split</p>
                      <p className="text-sm font-mono font-medium">{winnerSplit}%</p>
                    </div>
                  </div>
                </div>

                {/* Revenue Split Bar */}
                <div className="p-3 rounded-lg bg-secondary/20">
                  <p className="text-[10px] text-muted-foreground uppercase mb-2">Revenue Split</p>
                  <div className="flex h-3 rounded-full overflow-hidden">
                    <div className="bg-neon" style={{ width: `${winnerSplit}%` }} title={`Winner: ${winnerSplit}%`} />
                    <div className="bg-gold" style={{ width: `${hostCut}%` }} title={`Host: ${hostCut}%`} />
                    <div className="bg-blue-400" style={{ width: `${promoterCut}%` }} title={`Promoter: ${promoterCut}%`} />
                    <div className="bg-muted-foreground/30" style={{ width: "15%" }} title="Platform: 15%" />
                  </div>
                  <div className="flex justify-between mt-1.5 text-[10px] text-muted-foreground">
                    <span className="text-neon">Winner {winnerSplit}%</span>
                    <span className="text-gold">Host {hostCut}%</span>
                    <span className="text-blue-400">Promo {promoterCut}%</span>
                    <span>GP 15%</span>
                  </div>
                </div>

                {/* Social Distribution */}
                <div className="flex items-center gap-2">
                  <Share2 className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Distribution:</span>
                  <Badge variant="outline" className="text-[10px]">
                    {gameConfig.socialDistributionMode || "direct"}
                  </Badge>
                  {gameConfig.socialHashtag && (
                    <Badge variant="outline" className="text-[10px] text-neon border-neon/30">
                      #{gameConfig.socialHashtag}
                    </Badge>
                  )}
                </div>

                {/* Coupon */}
                <div className="space-y-2">
                  <label htmlFor="launch-coupon-code" className="text-xs font-medium text-muted-foreground uppercase">
                    Coupon Code
                  </label>
                  <Input
                    id="launch-coupon-code"
                    value={couponCode}
                    onChange={(e) => {
                      setCouponTouched(true);
                      setCouponCode(e.target.value.toUpperCase());
                    }}
                    placeholder="Optional"
                    className="bg-secondary/40"
                    maxLength={64}
                  />
                  {couponValid && (
                    <p className="text-xs text-neon flex items-center gap-1.5">
                      <CheckCircle2 className="w-3 h-3 shrink-0" />
                      {normalizedCoupon === betaQuery.data?.code
                        ? "Beta: the event fee is waived, so creating this event is free."
                        : `Coupon ${normalizedCoupon} applied.`}
                    </p>
                  )}
                  {couponError && (
                    <p className="text-xs text-destructive flex items-center gap-1.5">
                      <AlertTriangle className="w-3 h-3 shrink-0" />
                      {couponError}
                    </p>
                  )}
                </div>

                {/* Creation Fee & Wallet */}
                <div className={`p-3 rounded-lg border space-y-1 ${
                  insufficientForLaunch
                    ? "bg-destructive/10 border-destructive/30"
                    : "bg-secondary/20 border-transparent"
                }`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      <Wallet className="w-3.5 h-3.5" />
                      Event fee
                      {couponValid ? (
                        <Badge variant="outline" className="text-[9px] text-neon border-neon/40">
                          {normalizedCoupon}
                        </Badge>
                      ) : credits > 0 && baseFeeCents > 0 && (
                        <Badge variant="outline" className="text-[9px] text-neon border-neon/40">
                          Free credit ({credits})
                        </Badge>
                      )}
                    </span>
                    <span className="font-mono font-medium flex items-center gap-2">
                      {couponValid && baseFeeCents > creationFeeCents && (
                        <span className="line-through text-muted-foreground">
                          ${(baseFeeCents / 100).toFixed(2)}
                        </span>
                      )}
                      {creationFeeCents === 0 ? "Free" : `$${(creationFeeCents / 100).toFixed(2)}`}
                    </span>
                  </div>
                  {creationFeeCents > 0 && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Wallet balance</span>
                      <span className={`font-mono ${
                        insufficientForLaunch ? "text-destructive" : "text-foreground"
                      }`}>
                        {walletBalanceCents != null ? `$${(walletBalanceCents / 100).toFixed(2)}` : "…"}
                      </span>
                    </div>
                  )}
                  {insufficientForLaunch && (
                    <p className="text-xs text-destructive flex items-center gap-1.5 pt-1">
                      <AlertTriangle className="w-3 h-3 shrink-0" />
                      Not enough funds.{" "}
                      <Link href="/console/wallet" className="underline font-medium hover:text-destructive/80">
                        Top up your wallet
                      </Link>
                    </p>
                  )}
                </div>
              </div>

              <Separator className="bg-border/30" />

              {/* Validation Issues */}
              {validateMutation.data && !validateMutation.data.valid && (
                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 space-y-1">
                  <p className="text-xs font-semibold text-destructive flex items-center gap-1.5">
                    <XCircle className="w-3.5 h-3.5" />
                    Validation Issues
                  </p>
                  {validateMutation.data.issues.map((issue, i) => (
                    <p key={i} className="text-xs text-destructive/80 pl-5">• {issue}</p>
                  ))}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs gap-1.5"
                  onClick={handleValidate}
                  disabled={validateMutation.isPending}
                >
                  {validateMutation.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-3.5 h-3.5" />
                  )}
                  Validate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs gap-1.5"
                  onClick={handleCopyPayload}
                  disabled={!validateMutation.data?.config && !launchResult?.payload}
                >
                  {copied ? (
                    <Check className="w-3.5 h-3.5 text-green-400" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                  Copy JSON
                </Button>
                <Button
                  size="sm"
                  className="flex-1 bg-neon hover:bg-neon/90 text-background text-xs gap-1.5"
                  onClick={handleLaunch}
                  disabled={launchMutation.isPending || insufficientForLaunch || feeCheckPending}
                >
                  {launchMutation.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Rocket className="w-3.5 h-3.5" />
                  )}
                  Launch Now
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
